import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import ContentHeader from "../../components/Admin/ContentHeader";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { useGetMisConnection } from "../../hooks/Admin/MisSettings/useGetMisConnection";
import { usePutMisConnection } from "../../hooks/Admin/MisSettings/usePutMisConnection";
import { useTestMisConnection } from "../../hooks/Admin/MisSettings/useTestMisConnection";
import { useGetMisQueueSettings } from "../../hooks/Admin/MisSettings/useGetMisQueueSettings";
import { usePutMisQueueSettings } from "../../hooks/Admin/MisSettings/usePutMisQueueSettings";
import {
  useGetEmergencyCodes,
  useSaveEmergencyCode,
} from "../../hooks/Admin/MisSettings/useEmergencyCodes";
import {
  BenefitsTerminalMode,
  EmergencyCodeDto,
  MisAuthType,
  MisConnectionRequest,
  MisConnectionTestDto,
} from "../../types/Mis/MisTypes";
import classes from "./MisSettingsPage.module.scss";

const emptyConnection: MisConnectionRequest = {
  enabled: false,
  baseUrl: "",
  getBenefitsPath: "/api/queue/getbenefits",
  postBenefitsPath: "/api/queue/getPersonBenefits",
  formatParam: "",
  connectTimeoutSeconds: 5,
  readTimeoutSeconds: 10,
  sslInsecure: false,
  authType: "NONE",
  username: "",
  secret: "",
  authHeaderName: "",
  clientMode: "HTTP",
  localTest: false,
};

const emptyEmergency = {
  code: "",
  comment: "",
  isActive: true,
};

const MisSettingsPage = () => {
  const { canEdit } = useMyPermissions();
  const canEditMis = canEdit("SETTINGS_MIS");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const selectedBranchId = filterBranchIds[0] ?? null;
  const hasSelectedQueue = filterQueueIds.some((id) => id > 0);
  const branchQueueId = hasSelectedQueue
    ? (filterQueueIds.find((id) => id > 0) ?? null)
    : null;

  const { data: connection } = useGetMisConnection(selectedBranchId);
  const putConnection = usePutMisConnection(selectedBranchId);
  const testConnection = useTestMisConnection(selectedBranchId);
  const { data: queueSettings } = useGetMisQueueSettings(branchQueueId);
  const putQueue = usePutMisQueueSettings(branchQueueId);
  const { data: emergencyCodes = [] } = useGetEmergencyCodes(selectedBranchId);
  const saveEmergency = useSaveEmergencyCode(selectedBranchId);

  const [connectionForm, setConnectionForm] =
    useState<MisConnectionRequest>(emptyConnection);
  const [queueForm, setQueueForm] = useState({
    benefitsEnabled: false,
    otoEnabled: false,
    benefitsTerminalMode: "SELECT_CATEGORY" as BenefitsTerminalMode,
    benefitTicketColor: "#cfe2ff",
    otoTicketColor: "#f8d7da",
    otoConfirmationTimeoutSeconds: 180,
  });
  const [emergencyDrafts, setEmergencyDrafts] = useState<
    Record<number, EmergencyCodeDto>
  >({});
  const [newEmergency, setNewEmergency] = useState(emptyEmergency);
  const [testResult, setTestResult] = useState<MisConnectionTestDto | null>(
    null,
  );

  useEffect(() => {
    setTestResult(null);
    if (!connection) {
      setConnectionForm(emptyConnection);
      return;
    }
    setConnectionForm({
      enabled: connection.enabled,
      baseUrl: connection.baseUrl ?? "",
      getBenefitsPath: connection.getBenefitsPath ?? "/api/queue/getbenefits",
      postBenefitsPath:
        connection.postBenefitsPath ?? "/api/queue/getPersonBenefits",
      formatParam: connection.formatParam ?? "",
      connectTimeoutSeconds: connection.connectTimeoutSeconds ?? 5,
      readTimeoutSeconds: connection.readTimeoutSeconds ?? 10,
      sslInsecure: connection.sslInsecure,
      authType: connection.authType ?? "NONE",
      username: connection.username ?? "",
      secret: "",
      authHeaderName: connection.authHeaderName ?? "",
      clientMode: connection.clientMode ?? "HTTP",
      localTest: Boolean(connection.localTest),
    });
  }, [connection]);

  useEffect(() => {
    if (!queueSettings) {
      setQueueForm({
        benefitsEnabled: false,
        otoEnabled: false,
        benefitsTerminalMode: "SELECT_CATEGORY",
        benefitTicketColor: "#cfe2ff",
        otoTicketColor: "#f8d7da",
        otoConfirmationTimeoutSeconds: 180,
      });
      return;
    }
    setQueueForm({
      benefitsEnabled: queueSettings.benefitsEnabled,
      otoEnabled: queueSettings.otoEnabled,
      benefitsTerminalMode:
        queueSettings.benefitsTerminalMode ?? "SELECT_CATEGORY",
      benefitTicketColor: queueSettings.benefitTicketColor ?? "#cfe2ff",
      otoTicketColor: queueSettings.otoTicketColor ?? "#f8d7da",
      otoConfirmationTimeoutSeconds:
        queueSettings.otoConfirmationTimeoutSeconds ?? 180,
    });
  }, [queueSettings]);

  useEffect(() => {
    const next: Record<number, EmergencyCodeDto> = {};
    emergencyCodes.forEach((item) => {
      next[item.id] = { ...item };
    });
    setEmergencyDrafts(next);
  }, [emergencyCodes]);

  const buildConnectionPayload = (): MisConnectionRequest => {
    const secret = connectionForm.secret?.trim();
    return {
      ...connectionForm,
      baseUrl: connectionForm.baseUrl?.trim() || null,
      getBenefitsPath:
        connectionForm.getBenefitsPath?.trim() || "/api/queue/getbenefits",
      postBenefitsPath:
        connectionForm.postBenefitsPath?.trim() ||
        "/api/queue/getPersonBenefits",
      formatParam: connectionForm.formatParam?.trim() ?? "",
      username: connectionForm.username?.trim() || null,
      authHeaderName: connectionForm.authHeaderName?.trim() || null,
      clientMode: "HTTP",
      secret: secret ? secret : undefined,
    };
  };

  const handleTest = () => {
    if (selectedBranchId == null) return;
    setTestResult(null);
    testConnection.mutate(buildConnectionPayload(), {
      onSuccess: (response) => setTestResult(response.data),
    });
  };

  return (
    <div className={classes.container}>
      <ContentHeader
        name="МИС и льготы"
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            singleQueue
            queueIdsFilter={queueIdsFilter}
          />
        }
      />

      {selectedBranchId == null && (
        <div className={classes.section}>
          <p className={classes.noQueueSelectedHint}>
            Выберите филиал и очередь в фильтре, чтобы настроить МИС, льготы и
            CITO.
          </p>
        </div>
      )}

      {selectedBranchId != null && (
        <div className={classes.section}>
          <h5 className={classes.sectionTitle}>Подключение МИС (филиал)</h5>
          <Form.Check
            type="switch"
            label="Включить интеграцию с МИС"
            checked={Boolean(connectionForm.enabled)}
            disabled={!canEditMis}
            onChange={(e) =>
              setConnectionForm((prev) => ({
                ...prev,
                enabled: e.target.checked,
              }))
            }
          />
          <p className={classes.hint}>
            Если выключено, льготные талоны не печатаются. CITO может работать
            локально по цифровым кодам.
          </p>

          <div className={classes.row}>
            <label className={classes.field}>
              Адрес МИС
              <Form.Control
                value={connectionForm.baseUrl ?? ""}
                disabled={!canEditMis}
                placeholder="https://public.ehealth.by/iis-stage/eMedicoApi"
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    baseUrl: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <Form.Check
            type="checkbox"
            id="mis-local-test"
            label="Отображать строку ввода"
            className={classes.localTestCheck}
            checked={Boolean(connectionForm.localTest)}
            disabled={!canEditMis}
            onChange={(e) =>
              setConnectionForm((prev) => ({
                ...prev,
                localTest: e.target.checked,
              }))
            }
          />
          <p className={classes.hint}>
            С галочкой на терминале можно ввести номер карты с клавиатуры. Без
            галочки CITO вводится с экранной цифровой клавиатуры, строка ввода
            скрыта.
          </p>

          <div className={classes.row}>
            <label className={classes.field}>
              Путь GET льгот
              <Form.Control
                value={connectionForm.getBenefitsPath ?? ""}
                disabled={!canEditMis}
                placeholder="/api/queue/getbenefits"
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    getBenefitsPath: e.target.value,
                  }))
                }
              />
            </label>
            <label className={classes.field}>
              Путь POST льгот
              <Form.Control
                value={connectionForm.postBenefitsPath ?? ""}
                disabled={!canEditMis}
                placeholder="/api/queue/getPersonBenefits"
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    postBenefitsPath: e.target.value,
                  }))
                }
              />
            </label>
            <label className={`${classes.field} ${classes.fieldNarrow}`}>
              Параметр _format
              <Form.Control
                value={connectionForm.formatParam ?? ""}
                disabled={!canEditMis}
                placeholder="пусто для eMedico"
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    formatParam: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <p className={classes.hint}>
            GET /api/queue/getbenefits возвращает массив{" "}
            <code>{`{ "Id": 1, "Name": "Дети-инвалиды" }`}</code>. POST уходит
            как JSON <code>{`{ "Identifier": "<номер карты>" }`}</code> на
            /api/queue/getPersonBenefits. Параметр _format оставьте пустым.
          </p>

          <div className={classes.row}>
            <label className={`${classes.field} ${classes.fieldNarrow}`}>
              Таймаут соединения, сек
              <Form.Control
                type="number"
                min={1}
                value={connectionForm.connectTimeoutSeconds ?? 5}
                disabled={!canEditMis}
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    connectTimeoutSeconds: Number(e.target.value) || 5,
                  }))
                }
              />
            </label>
            <label className={`${classes.field} ${classes.fieldNarrow}`}>
              Таймаут чтения, сек
              <Form.Control
                type="number"
                min={1}
                value={connectionForm.readTimeoutSeconds ?? 10}
                disabled={!canEditMis}
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    readTimeoutSeconds: Number(e.target.value) || 10,
                  }))
                }
              />
            </label>
            <Form.Check
              type="switch"
              label="Не проверять TLS-сертификат"
              checked={Boolean(connectionForm.sslInsecure)}
              disabled={!canEditMis}
              onChange={(e) =>
                setConnectionForm((prev) => ({
                  ...prev,
                  sslInsecure: e.target.checked,
                }))
              }
            />
          </div>

          <div className={classes.row}>
            <label className={classes.field}>
              Авторизация
              <Form.Select
                value={connectionForm.authType}
                disabled={!canEditMis}
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    authType: e.target.value as MisAuthType,
                  }))
                }
              >
                <option value="NONE">Без авторизации</option>
                <option value="BASIC">Basic</option>
                <option value="HEADER">Заголовок</option>
              </Form.Select>
            </label>
            {connectionForm.authType !== "NONE" && (
              <label className={classes.field}>
                {connectionForm.authType === "HEADER"
                  ? "Имя заголовка / логин"
                  : "Логин"}
                <Form.Control
                  value={
                    connectionForm.authType === "HEADER"
                      ? (connectionForm.authHeaderName ?? "")
                      : (connectionForm.username ?? "")
                  }
                  disabled={!canEditMis}
                  onChange={(e) =>
                    setConnectionForm((prev) =>
                      prev.authType === "HEADER"
                        ? { ...prev, authHeaderName: e.target.value }
                        : { ...prev, username: e.target.value },
                    )
                  }
                />
              </label>
            )}
            {connectionForm.authType !== "NONE" && (
              <label className={classes.field}>
                Пароль / секрет
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    connection?.passwordSet
                      ? "Задан, введите чтобы сменить"
                      : ""
                  }
                  value={connectionForm.secret ?? ""}
                  disabled={!canEditMis}
                  onChange={(e) =>
                    setConnectionForm((prev) => ({
                      ...prev,
                      secret: e.target.value,
                    }))
                  }
                />
              </label>
            )}
          </div>

          {testResult && (
            <div
              className={
                testResult.success ? classes.successBox : classes.errorBox
              }
            >
              <div>{testResult.message}</div>
              {testResult.categories?.length > 0 && (
                <ul>
                  {testResult.categories.map((item) => (
                    <li key={`${item.code}-${item.display}`}>
                      {item.code}
                      {item.display ? ` — ${item.display}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {canEditMis && (
            <div className={classes.actions}>
              <CustomButton
                content={
                  testConnection.isLoading
                    ? "Проверка..."
                    : "Проверить соединение"
                }
                styleType="default"
                onClick={() => {
                  if (testConnection.isLoading) return;
                  handleTest();
                }}
              />
              <CustomButton
                content={
                  putConnection.isLoading
                    ? "Сохранение..."
                    : "Сохранить подключение"
                }
                styleType="active"
                onClick={() => {
                  if (putConnection.isLoading || selectedBranchId == null) {
                    return;
                  }
                  putConnection.mutate(buildConnectionPayload());
                }}
              />
            </div>
          )}
        </div>
      )}

      {branchQueueId != null && (
        <div className={classes.section}>
          <h5 className={classes.sectionTitle}>Льготы и CITO (очередь)</h5>
          <div className={classes.flags}>
            <span
              className={`${classes.flag} ${
                queueSettings?.misEnabled ? classes.flagOn : ""
              }`}
            >
              МИС {queueSettings?.misEnabled ? "включена" : "выключена"}
            </span>
            <span
              className={`${classes.flag} ${
                queueSettings?.benefitsEffective ? classes.flagOn : ""
              }`}
            >
              Льготы{" "}
              {queueSettings?.benefitsEffective ? "работают" : "не активны"}
            </span>
            <span
              className={`${classes.flag} ${
                queueSettings?.otoEffective ? classes.flagOn : ""
              }`}
            >
              CITO {queueSettings?.otoEffective ? "работает" : "не активно"}
            </span>
          </div>
          <Form.Check
            type="switch"
            label="Льготная очередь (подтверждение через МИС)"
            checked={queueForm.benefitsEnabled}
            disabled={!canEditMis}
            onChange={(e) =>
              setQueueForm((prev) => ({
                ...prev,
                benefitsEnabled: e.target.checked,
              }))
            }
          />
          {queueForm.benefitsEnabled && (
            <div className={classes.modeGroup}>
              <div className={classes.modeTitle}>
                Как посетитель подтверждает льготу
              </div>
              <Form.Check
                type="radio"
                id="benefits-mode-select-category"
                name="benefitsTerminalMode"
                label="Выбор категории льготы"
                checked={queueForm.benefitsTerminalMode === "SELECT_CATEGORY"}
                disabled={!canEditMis}
                onChange={() =>
                  setQueueForm((prev) => ({
                    ...prev,
                    benefitsTerminalMode: "SELECT_CATEGORY",
                  }))
                }
              />
              <p className={classes.hint}>
                На терминале плитки категорий берутся из справочника МИС (GET
                getbenefits). Посетитель выбирает тип, затем сканирует карту.
              </p>
              <Form.Check
                type="radio"
                id="benefits-mode-scan-card"
                name="benefitsTerminalMode"
                label="Определить льготу по карте"
                checked={queueForm.benefitsTerminalMode === "SCAN_CARD"}
                disabled={!canEditMis}
                onChange={() =>
                  setQueueForm((prev) => ({
                    ...prev,
                    benefitsTerminalMode: "SCAN_CARD",
                  }))
                }
              />
              <p className={classes.hint}>
                Типы льгот на терминале не показываются. Есть одна кнопка
                «Сканировать медкарту»: МИС сама определяет льготу по карте.
              </p>
              <Form.Check
                type="radio"
                id="benefits-mode-scan-on-service"
                name="benefitsTerminalMode"
                label="Скан при выборе услуги"
                checked={queueForm.benefitsTerminalMode === "SCAN_ON_SERVICE"}
                disabled={!canEditMis}
                onChange={() =>
                  setQueueForm((prev) => ({
                    ...prev,
                    benefitsTerminalMode: "SCAN_ON_SERVICE",
                  }))
                }
              />
              <p className={classes.hint}>
                Меню услуг как обычно. После выбора услуги открывается окно:
                отсканировать медкарту, получить талон без льготы или отменить.
              </p>
            </div>
          )}
          <Form.Check
            type="switch"
            label="Экстренная очередь (CITO)"
            checked={queueForm.otoEnabled}
            disabled={!canEditMis}
            onChange={(e) =>
              setQueueForm((prev) => ({
                ...prev,
                otoEnabled: e.target.checked,
              }))
            }
          />
          <p className={classes.hint}>
            Если включено, на терминале показывается плитка «Экстренная очередь
            (CITO)». Льготы нельзя включить без работающей МИС.
          </p>
          <div className={classes.row}>
            <label className={`${classes.field} ${classes.fieldNarrow}`}>
              Таймаут подтверждения CITO, сек
              <Form.Control
                type="number"
                min={5}
                max={3600}
                value={queueForm.otoConfirmationTimeoutSeconds}
                disabled={!canEditMis}
                onChange={(e) =>
                  setQueueForm((prev) => ({
                    ...prev,
                    otoConfirmationTimeoutSeconds:
                      Number(e.target.value) || 180,
                  }))
                }
              />
            </label>
          </div>
          <p className={classes.hint}>
            После сообщения «CITO подтверждено» подтверждение действует
            указанное число секунд. Затем оно аннулируется, и следующий талон
            печатается без CITO. Допустимый диапазон — от 5 до 3600 секунд.
          </p>
          <div className={classes.colorRow}>
            <div className={classes.colorField}>
              <Form.Label>Цвет льготных талонов</Form.Label>
              <div className={classes.colorControls}>
                <Form.Control
                  type="color"
                  value={queueForm.benefitTicketColor}
                  disabled={!canEditMis}
                  onChange={(e) =>
                    setQueueForm((prev) => ({
                      ...prev,
                      benefitTicketColor: e.target.value,
                    }))
                  }
                />
                <Form.Control
                  type="text"
                  value={queueForm.benefitTicketColor}
                  disabled={!canEditMis}
                  maxLength={7}
                  onChange={(e) =>
                    setQueueForm((prev) => ({
                      ...prev,
                      benefitTicketColor: e.target.value,
                    }))
                  }
                />
                <span
                  className={classes.colorPreview}
                  style={{ backgroundColor: queueForm.benefitTicketColor }}
                />
              </div>
            </div>
            <div className={classes.colorField}>
              <Form.Label>Цвет талонов CITO</Form.Label>
              <div className={classes.colorControls}>
                <Form.Control
                  type="color"
                  value={queueForm.otoTicketColor}
                  disabled={!canEditMis}
                  onChange={(e) =>
                    setQueueForm((prev) => ({
                      ...prev,
                      otoTicketColor: e.target.value,
                    }))
                  }
                />
                <Form.Control
                  type="text"
                  value={queueForm.otoTicketColor}
                  disabled={!canEditMis}
                  maxLength={7}
                  onChange={(e) =>
                    setQueueForm((prev) => ({
                      ...prev,
                      otoTicketColor: e.target.value,
                    }))
                  }
                />
                <span
                  className={classes.colorPreview}
                  style={{ backgroundColor: queueForm.otoTicketColor }}
                />
              </div>
            </div>
          </div>
          <p className={classes.hint}>
            Эти цвета используются в интерфейсе оператора для подсветки фона
            льготных и экстренных талонов в очереди.
          </p>
          {canEditMis && (
            <div className={classes.actions}>
              <CustomButton
                content={
                  putQueue.isLoading
                    ? "Сохранение..."
                    : "Сохранить настройки очереди"
                }
                styleType="active"
                onClick={() => {
                  if (putQueue.isLoading || branchQueueId == null) return;
                  const timeout = Math.min(
                    3600,
                    Math.max(
                      5,
                      Number(queueForm.otoConfirmationTimeoutSeconds) || 180,
                    ),
                  );
                  putQueue.mutate({
                    ...queueForm,
                    otoConfirmationTimeoutSeconds: timeout,
                  });
                }}
              />
            </div>
          )}
        </div>
      )}

      {selectedBranchId != null && (
        <div className={classes.section}>
          <h5 className={classes.sectionTitle}>Коды CITO</h5>
          <p className={classes.hint}>
            Цифровой код, который сотрудник вводит или сканирует на терминале
            для экстренного талона.
          </p>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Комментарий</th>
                  <th>Активен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.values(emergencyDrafts).map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Form.Control
                        value={row.code}
                        disabled={!canEditMis}
                        onChange={(e) =>
                          setEmergencyDrafts((prev) => ({
                            ...prev,
                            [row.id]: { ...row, code: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        value={row.comment ?? ""}
                        disabled={!canEditMis}
                        onChange={(e) =>
                          setEmergencyDrafts((prev) => ({
                            ...prev,
                            [row.id]: { ...row, comment: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <Form.Check
                        checked={row.isActive}
                        disabled={!canEditMis}
                        onChange={(e) =>
                          setEmergencyDrafts((prev) => ({
                            ...prev,
                            [row.id]: { ...row, isActive: e.target.checked },
                          }))
                        }
                      />
                    </td>
                    <td>
                      {canEditMis && (
                        <CustomButton
                          content="Сохранить"
                          styleType="default"
                          onClick={() =>
                            saveEmergency.mutate({
                              id: row.id,
                              payload: {
                                code: row.code,
                                comment: row.comment,
                                isActive: row.isActive,
                              },
                            })
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEditMis && (
            <div className={classes.addRow}>
              <Form.Control
                placeholder="Код"
                value={newEmergency.code}
                onChange={(e) =>
                  setNewEmergency((prev) => ({ ...prev, code: e.target.value }))
                }
              />
              <Form.Control
                placeholder="Комментарий"
                value={newEmergency.comment}
                onChange={(e) =>
                  setNewEmergency((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
              />
              <CustomButton
                content="Добавить"
                styleType="active"
                onClick={() => {
                  if (!newEmergency.code.trim()) return;
                  saveEmergency.mutate(
                    { payload: newEmergency },
                    { onSuccess: () => setNewEmergency(emptyEmergency) },
                  );
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MisSettingsPage;
